import '@/assets/styles/views/solver/passed-questions-modal.styles.scss';
import { Modal } from '@/components';
import { useSolveQuestion } from '@/context/solve-question-context';
import { useRef, useState, useEffect } from 'react';
import 'swiper/css';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import clsx from 'clsx';
import { LocalStorageKeys } from '@/constants/local-storage-keys.ts';

const QuestionSlide = ({ question, activeSlideIndex, questionPoolLength }: any) => {
	return (
		<>
			<div
				className="slide-content"
				style={{
					background: `linear-gradient(180deg, rgba(0, 0, 0, 0.00) 50%, rgba(0, 0, 0, 0.50) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), url('${question?.questionImage}') lightgray 50% / cover no-repeat`,
					backgroundSize: 'contain'
				}}>
				<div className="question-info">
					<span className="question-subject">
						Soru: {activeSlideIndex + 1}/{questionPoolLength}
					</span>
				</div>
				<span className="question-topic">{question?.curriculumName}</span>
			</div>
		</>
	);
};

const PassedQuestionsModal = () => {
	const {
		isActivePassedQuestions,
		onClosePassedQuestions,
		passedQuestionPool,
		activeSlideIndex,
		setActiveSlideIndex,
		setIsOpenSolveBottomSheet,
		activeTimer,
		setIsDisabledQuestionTimer,
		handleStartSolving: contextHandleStartSolving,
		setQuestionIsAccepted
	} = useSolveQuestion();

	const [slideSettings, setSlideSettings] = useState({
		slidesPerView: 1.35,
		spaceBetween: 30
	});

	const swiperRef = useRef<SwiperType | null>(null);

	const handleSlideChange = (swiper: SwiperType) => {
		const newIndex = swiper.activeIndex;
		if (newIndex !== activeSlideIndex) {
			setActiveSlideIndex(newIndex);
		}
	};

	const handleSwiperInit = (swiper: SwiperType) => {
		swiperRef.current = swiper;
	};

	const handlePrevious = () => {
		if (swiperRef.current && activeSlideIndex > 0) {
			swiperRef.current.slideTo(activeSlideIndex - 1);
		}
	};

	const handleNext = () => {
		if (swiperRef.current && activeSlideIndex < passedQuestionPool?.length - 1) {
			swiperRef.current.slideTo(activeSlideIndex + 1);
		}
	};

	const onStartSolving = () => {
		setIsOpenSolveBottomSheet(true);
	};

	const onOpenSolveBottomSheet = () => {
		setIsDisabledQuestionTimer(true);
		setIsOpenSolveBottomSheet(true);
	};

	const handleSolveQuestion = async () => {
		activeTimer.clearTimer();
		localStorage.removeItem(LocalStorageKeys.ACTIVE_QUESTION_TIMER);
		localStorage.removeItem(LocalStorageKeys.SOLVER_TIMERS);

		const currentQuestion = passedQuestionPool?.[activeSlideIndex];
		setQuestionIsAccepted(true);

		await contextHandleStartSolving(currentQuestion.questionId, currentQuestion.interactionId);

		onClosePassedQuestions();

		onOpenSolveBottomSheet();

		const timerInfo = activeTimer.getTimerInfo();
		if (timerInfo && timerInfo.remainingTime > 0) {
			onStartSolving();
		}
	};

	useEffect(() => {
		const updateSlideSettings = () => {
			const width = window.innerWidth;

			if (width < 400) {
				setSlideSettings({ slidesPerView: 1.3, spaceBetween: 120 });
			} else if (width < 415) {
				setSlideSettings({ slidesPerView: 1.3, spaceBetween: 20 });
			} else if (width < 480) {
				setSlideSettings({ slidesPerView: 1.35, spaceBetween: 30 });
			} else if (width < 768) {
				setSlideSettings({ slidesPerView: 1.3, spaceBetween: 20 });
			} else if (width < 1024) {
				setSlideSettings({ slidesPerView: 1.4, spaceBetween: 25 });
			} else {
				setSlideSettings({ slidesPerView: 1, spaceBetween: 30 });
			}
		};

		updateSlideSettings();
		window.addEventListener('resize', updateSlideSettings);

		return () => window.removeEventListener('resize', updateSlideSettings);
	}, []);

	return (
		<Modal
			animationDuration={0}
			variant="centered"
			overlayClassName="passed-questions-modal-overlay"
			className="passed-questions-modal"
			isOpen={isActivePassedQuestions && !!passedQuestionPool?.length}
			onClose={onClosePassedQuestions}>
			<div className="passed-questions-wrapper">
				<button
					className="close-button"
					onClick={onClosePassedQuestions}>
					<i className="icons-x" />
				</button>

				<div className="swiper-container">
					<Swiper
						spaceBetween={slideSettings.spaceBetween}
						slidesPerView={slideSettings.slidesPerView}
						centeredSlides={true}
						className="question-swiper"
						onSlideChange={handleSlideChange}
						onSwiper={handleSwiperInit}
						allowTouchMove={true}
						initialSlide={activeSlideIndex}>
						{passedQuestionPool?.map((question, index) => (
							<SwiperSlide key={question?.interactionId || index}>
								<QuestionSlide
									question={question}
									activeSlideIndex={activeSlideIndex}
									questionPoolLength={passedQuestionPool?.length || 0}
								/>
							</SwiperSlide>
						))}
					</Swiper>
				</div>

				<div className="modal-actions">
					<button
						className={clsx('desktop', 'btn-previous', activeSlideIndex === 0 && 'invisible')}
						onClick={handlePrevious}
						disabled={activeSlideIndex === 0}>
						<i className="icons-chevron-left" />
						Önceki
					</button>

					<button
						className="btn-solve"
						onClick={handleSolveQuestion}>
						Soruyu Çöz, 1 Pas Hakkı Kazan
					</button>

					<button
						className={clsx('desktop', 'btn-next', activeSlideIndex === passedQuestionPool?.length - 1 && 'invisible')}
						onClick={handleNext}
						disabled={activeSlideIndex === passedQuestionPool?.length - 1}>
						Sonraki
						<i className="icons-chevron-right" />
					</button>
				</div>
			</div>
		</Modal>
	);
};

export default PassedQuestionsModal;
